'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useEffect } from 'react';

interface ReviewersHeroProps {
  onCTAClick: () => void;
}

const steps = [
  { step: '01', title: 'Download App & Sign Up', desc: 'Create your profile, complete KYC in 5 minutes.', color: '#FF5722', bg: '#FFF3EE' },
  { step: '02', title: 'Pick Products to Review', desc: 'Browse products submitted by brands and pick what interests you.', color: '#F59E0B', bg: '#FFFBEB' },
  { step: '03', title: 'Review & Get Paid', desc: 'Test the product, post your honest review, earn via UPI within 7 days.', color: '#10B981', bg: '#ECFDF5' },
];

export default function ReviewersHero({ onCTAClick }: ReviewersHeroProps) {
  const [reviewerCount, setReviewerCount] = useState(0);

  useEffect(() => {
    async function fetchCount() {
      const supabase = createClient();
      try {
        const { count } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true })
          .eq('role', 'reviewer');
        setReviewerCount(count || 0);
      } catch (err) {
        console.log('Reviewer count fetch error:', err);
      }
    }
    fetchCount();
  }, []);

  const quickStats = [
    { value: reviewerCount > 0 ? `${(reviewerCount / 1000).toFixed(0)}K+` : '50,000+', label: 'Active Reviewers' },
    { value: '₹1,200', label: 'Avg. Monthly Earn' },
    { value: '7 days', label: 'UPI Payout Time' },
  ];

  return (
    <section className="relative pt-16 overflow-hidden bg-white">
      {/* Dot grid */}
      <div className="absolute inset-0 dot-grid-bg opacity-50 pointer-events-none" />

      {/* Color blobs */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,87,34,0.06) 0%, transparent 70%)', filter: 'blur(80px)' }} />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.05) 0%, transparent 70%)', filter: 'blur(60px)' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-7">
            <div className="inline-flex items-center gap-2 bg-primary-container rounded-full px-4 py-2 border border-primary/15">
              <Icon name="CurrencyRupeeIcon" size={14} className="text-primary" />
              <span className="text-xs font-bold text-primary uppercase tracking-widest">For Reviewers</span>
            </div>
            <h1 className="font-heading font-bold text-foreground" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', lineHeight: 1.1 }}>
              Pick Products,
              <br />
              <span className="gradient-text">Review & Earn</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed max-w-xl">
              Brands submit products for honest testing and feedback. You pick what you love, review it, and earn ₹500–₹5,000 per month via UPI.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={onCTAClick}
                className="cta-gradient text-white font-heading font-bold px-8 py-4 rounded-xl hover:opacity-90 transition-colors text-sm shadow-lg shadow-primary/20"
              >
                Start Picking & Earning
              </button>
            </div>

            <div className="flex flex-wrap gap-8 pt-2">
              {quickStats.map((s) => (
                <div key={s.label}>
                  <div className="font-heading font-bold text-xl text-foreground">{s.value}</div>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white rounded-3xl p-7 shadow-premium border border-border space-y-5">
              <div className="font-heading font-bold text-foreground text-base mb-2">How to start earning</div>
              {steps.map((s, i) => (
                <div key={s.step} className="flex gap-4">
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: s.bg }}
                    >
                      <span className="font-heading font-bold text-xs" style={{ color: s.color }}>{s.step}</span>
                    </div>
                    {i < steps.length - 1 && <div className="w-px flex-1 bg-border" style={{ minHeight: '20px' }} />}
                  </div>
                  <div className="pb-2">
                    <div className="font-heading font-semibold text-foreground text-sm">{s.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, #F7F8FA)' }} />
    </section>
  );
}